import { FormEvent, useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { RRule, Weekday } from 'rrule'
import { useChurch, useCampuses } from '../hooks/useChurches'
import { useCreateEvent, useSubscriptionStatus } from '../hooks/useChurchAdmin'
import { ApiError } from '../lib/api'
import { EventTypePicker } from '../components/event/EventTypePicker'
import { EventTimePicker, DatePicker } from '../components/ui/DateTimePicker'
import { isPastDateTime } from '../lib/dates'
import { getDenominationConfig } from '@fellowship/shared'
import { AdminShell } from '../components/admin/AdminShell'
import { AdminPageHeader, AdminCard, AdmBtn, FormField, TextInput, AdminTextArea, Toggle } from '../components/admin/AdminAtoms'
import { AdmIcn } from '../components/admin/AdminIcons'
import type { EventType, Campus, Church } from '@fellowship/shared'
import LocationPicker from '../components/LocationPicker'
import { ImageUpload } from '../components/admin/ImageUpload'
import { CapabilityToggleRow } from '../components/event/CapabilityToggleRow'
import { DishGoalsFields } from '../components/event/DishGoalsFields'
import { OutdoorDetailsFields } from '../components/event/OutdoorDetailsFields'
import { MissingFieldsNotice } from '../components/event/MissingFieldsNotice'
import { EventPreviewModal } from '../components/admin/EventPreviewModal'
import { SubscriptionRequiredNotice } from '../components/admin/SubscriptionRequiredNotice'

// ─── RRule helpers ────────────────────────────────────────────────────────────

const DAYS: { label: string; rruleDay: Weekday }[] = [
  { label: 'Sun', rruleDay: RRule.SU },
  { label: 'Mon', rruleDay: RRule.MO },
  { label: 'Tue', rruleDay: RRule.TU },
  { label: 'Wed', rruleDay: RRule.WE },
  { label: 'Thu', rruleDay: RRule.TH },
  { label: 'Fri', rruleDay: RRule.FR },
  { label: 'Sat', rruleDay: RRule.SA },
]

function buildRRule(freq: 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY', byDay: number[]): string {
  if (freq === 'MONTHLY') return 'FREQ=MONTHLY'
  const interval = freq === 'BIWEEKLY' ? 2 : 1
  const days = byDay.map((i) => DAYS[i].rruleDay)
  const rule = new RRule({ freq: RRule.WEEKLY, interval, byweekday: days.length > 0 ? days : undefined })
  return rule.toString().replace(/^DTSTART[^\n]*\n?/m, '').replace(/^RRULE:/m, '')
}

function previewDates(rruleStr: string, startsAt: string): Date[] {
  if (!rruleStr || !startsAt) return []
  try {
    const rule = RRule.fromString(`DTSTART:${startsAt.replace(/[-:]/g, '').split('.')[0]}Z\nRRULE:${rruleStr}`)
    return rule.all((_, i) => i < 5)
  } catch {
    return []
  }
}


// ─── Location helpers ─────────────────────────────────────────────────────────

function churchAddress(church: Church): string {
  return [church.addressLine1, church.city, church.state, church.zip].filter(Boolean).join(', ')
}

function resolveLocationAddress(id: string, church: Church, campuses: Campus[], custom: string): string {
  if (id === 'custom') return custom || churchAddress(church)
  const campus = campuses.find((c) => `campus-${c.id}` === id)
  if (campus) {
    const parts = [campus.addressLine1, campus.city, campus.state, campus.zip].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : churchAddress(church)
  }
  return churchAddress(church)
}

// The venue name we store with the event. At the church building / a campus we save
// that place's name so the event reads "at <church>" (the read query no longer falls
// back to the church name via COALESCE). For a custom off-site location we save the
// host-typed venue name, or none — so it isn't mislabelled as the church.
function resolveStoredLocationName(id: string, church: Church, campuses: Campus[], custom: string): string | undefined {
  if (id === 'custom') return custom.trim() || undefined
  const campus = campuses.find((c) => `campus-${c.id}` === id)
  if (campus) return campus.name
  return church.name
}

function resolveLocationLat(id: string, church: Church, campuses: Campus[], custom: number | null): number {
  if (id === 'custom') return custom ?? church.locationLat
  const campus = campuses.find((c) => `campus-${c.id}` === id)
  if (campus?.locationLat != null) return campus.locationLat
  return church.locationLat
}

function resolveLocationLng(id: string, church: Church, campuses: Campus[], custom: number | null): number {
  if (id === 'custom') return custom ?? church.locationLng
  const campus = campuses.find((c) => `campus-${c.id}` === id)
  if (campus?.locationLng != null) return campus.locationLng
  return church.locationLng
}

// The venue/place name (e.g. "Coolidge Park"), distinct from the host church.
// Campuses carry their own name; a custom location uses whatever the picker
// captured. The main church address has no separate venue name — the church
// name is already surfaced on its own.
function resolveLocationName(id: string, campuses: Campus[], custom: string): string | undefined {
  if (id === 'custom') return custom.trim() || undefined
  const campus = campuses.find((c) => `campus-${c.id}` === id)
  return campus?.name
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function CreateEventPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { data: churchData } = useChurch(slug)
  const church = churchData?.data
  const { data: campusData } = useCampuses(church?.id)
  const campuses = campusData?.data ?? []
  const { data: subData, isLoading: subLoading } = useSubscriptionStatus(church?.id)
  const subscriptionActive =
    subData?.data?.status === 'active' || subData?.data?.status === 'trialing'
  const createEvent = useCreateEvent(slug ?? '')
  const denomConfig = getDenominationConfig(church?.denomination)

  const [eventType, setEventType] = useState<EventType>('potluck')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [tags, setTags] = useState('')
  const [maxAttendees, setMaxAttendees] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [isRecurring, setIsRecurring] = useState(false)
  const [freq, setFreq] = useState<'WEEKLY' | 'BIWEEKLY' | 'MONTHLY'>('WEEKLY')
  const [selectedDays, setSelectedDays] = useState<number[]>([])
  const [daysCustomized, setDaysCustomized] = useState(false)
  const [recurrenceEndsOn, setRecurrenceEndsOn] = useState('')
  const [mainGoal, setMainGoal] = useState(0)
  const [saladGoal, setSaladGoal] = useState(0)
  const [sideGoal, setSideGoal] = useState(0)
  const [dessertGoal, setDessertGoal] = useState(0)
  const [drinkGoal, setDrinkGoal] = useState(0)
  const [outdoorDifficulty, setOutdoorDifficulty] = useState<'easy' | 'moderate' | 'strenuous'>('easy')
  const [outdoorDistance, setOutdoorDistance] = useState('')
  const [outdoorElevation, setOutdoorElevation] = useState('')
  const [outdoorMeetupNote, setOutdoorMeetupNote] = useState('')
  const [studyTopic, setStudyTopic] = useState('')
  const [studyScripture, setStudyScripture] = useState('')
  const [attributes, setAttributes] = useState<Record<string, boolean | undefined>>({ bringADish: true })
  const [remindDayBefore, setRemindDayBefore] = useState(true)
  const [remindMorningOf, setRemindMorningOf] = useState(true)
  const [remindDishGap, setRemindDishGap] = useState(false)
  const [audience, setAudience] = useState<'everyone' | 'denomination'>('everyone')
  const [rsvpRequiresApproval, setRsvpRequiresApproval] = useState(false)
  const [hideAddressUntilApproved, setHideAddressUntilApproved] = useState(false)
  // 'church' = main church address, 'campus-{id}' = specific campus, 'custom' = freeform
  const [selectedLocationId, setSelectedLocationId] = useState<string>('church')
  const [customName, setCustomName] = useState('')
  const [customAddress, setCustomAddress] = useState('')
  const [customLat, setCustomLat] = useState<number | null>(null)
  const [customLng, setCustomLng] = useState<number | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (selectedLocationId !== 'custom' || !church) return
    if (customAddress === '') {
      setCustomAddress([church.addressLine1, church.city, church.state, church.zip].filter(Boolean).join(', '))
    }
    if (customLat === null) setCustomLat(church.locationLat)
    if (customLng === null) setCustomLng(church.locationLng)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, church])

  // Default the recurring day to the church's primary gathering day (Saturday
  // for SDA, Sunday for most others) once the church/config loads — unless the
  // user has already picked days themselves.
  useEffect(() => {
    if (!daysCustomized) setSelectedDays([denomConfig.worshipDay])
  }, [denomConfig.worshipDay, daysCustomized])

  const rruleStr = isRecurring ? buildRRule(freq, selectedDays) : ''
  const preview = isRecurring ? previewDates(rruleStr, startsAt) : []

  function toggleDay(idx: number) {
    setDaysCustomized(true)
    setSelectedDays((prev) =>
      prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx],
    )
  }

  function validate(): boolean {
    const errs: Record<string, string> = {}
    if (!title.trim()) errs.title = 'Title is required'
    if (!startsAt) errs.startsAt = 'Start date and time is required'
    else if (isPastDateTime(startsAt)) errs.startsAt = "Start time can't be in the past"
    if (isRecurring && freq !== 'MONTHLY' && selectedDays.length === 0)
      errs.days = 'Select at least one day'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function handleTypeChange(key: EventType) {
    setEventType(key)
    setAttributes((prev) => ({
      ...prev,
      bringADish: key === 'potluck' ? true : undefined,
      outdoorActivity: key === 'outdoors' ? true : undefined,
    }))
  }

  function buildMetadata() {
    const meta: Record<string, unknown> = {}
    if (attributes.bringADish) {
      const dishGoals = {
        ...(mainGoal > 0 ? { main: mainGoal } : {}),
        ...(saladGoal > 0 ? { salad: saladGoal } : {}),
        ...(dessertGoal > 0 ? { dessert: dessertGoal } : {}),
        ...(drinkGoal > 0 ? { drink: drinkGoal } : {}),
        ...(sideGoal > 0 ? { side: sideGoal } : {}),
      }
      if (Object.keys(dishGoals).length > 0) meta.dishGoals = dishGoals
    }
    if (attributes.outdoorActivity) {
      meta.difficulty = outdoorDifficulty
      if (outdoorDistance) meta.distanceMiles = parseFloat(outdoorDistance)
      if (outdoorElevation) meta.elevationFt = parseInt(outdoorElevation)
      if (outdoorMeetupNote.trim()) meta.meetupNote = outdoorMeetupNote.trim()
    }
    if (eventType === 'study') {
      if (studyTopic.trim()) meta.topic = studyTopic.trim()
      if (studyScripture.trim()) meta.scriptureRef = studyScripture.trim()
    }
    return Object.keys(meta).length > 0 ? meta : undefined
  }

  function buildPayload(status: 'active' | 'draft' = 'active') {
    if (!church) return null
    const parsedTags = tags.split(',').map((t) => t.trim()).filter(Boolean)
    const isCustomLocation = selectedLocationId === 'custom'
    return {
      title: title.trim(),
      description: description.trim() || undefined,
      eventType,
      locationName: resolveStoredLocationName(selectedLocationId, church, campuses, customName),
      addressText: resolveLocationAddress(selectedLocationId, church, campuses, customAddress),
      locationLat: resolveLocationLat(selectedLocationId, church, campuses, customLat),
      locationLng: resolveLocationLng(selectedLocationId, church, campuses, customLng),
      visibility: 'public' as const,
      audience,
      status,
      rsvpRequiresApproval,
      // Hiding the location is only offered for a custom off-site address (e.g. a
      // member's home). The church building / campuses are public places.
      hideAddressUntilApproved: isCustomLocation && rsvpRequiresApproval && hideAddressUntilApproved,
      maxAttendees: maxAttendees ? parseInt(maxAttendees) : undefined,
      isRecurring,
      rrule: rruleStr || undefined,
      recurrenceEndsOn: recurrenceEndsOn ? new Date(recurrenceEndsOn).toISOString() : undefined,
      tags: parsedTags,
      imageUrl: imageUrl ?? undefined,
      metadata: buildMetadata(),
      startsAt: new Date(startsAt).toISOString(),
      endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,
      churchId: church.id,
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!validate() || !church) return
    const payload = buildPayload('active')
    if (!payload) return
    createEvent.mutate(payload, { onSuccess: () => navigate(`/churches/${slug}/dashboard`) })
  }

  function handleSaveDraft() {
    if (!church) return
    // Minimal validation for draft — just needs a title
    if (!title.trim()) { setErrors({ title: 'Title is required' }); return }
    if (!startsAt) { setErrors({ startsAt: 'Date is required even for drafts' }); return }
    if (isPastDateTime(startsAt)) { setErrors({ startsAt: "Start time can't be in the past" }); return }
    const payload = buildPayload('draft')
    if (!payload) return
    createEvent.mutate(payload, {
      onSuccess: (res) => navigate(`/events/${res.data.event.id}`),
    })
  }

  const [showPreview, setShowPreview] = useState(false)
  const serverError = createEvent.error instanceof ApiError ? createEvent.error.message : null

  // Event creation is gated behind an active subscription. If a church admin
  // lands here without one (e.g. via "Host as a church" → pick church), show a
  // clear explanation instead of a form they can't submit — see issue #88.
  if (church && !subLoading && !subscriptionActive) {
    return (
      <AdminShell>
        <AdminPageHeader
          breadcrumb={['Events', 'New event']}
          eyebrow="Create"
          title="New event"
          sub="Hosting events as a church requires an active subscription."
        />
        <SubscriptionRequiredNotice
          slug={slug ?? ''}
          title={`${church.name} doesn't have an active subscription`}
          message="Start a subscription to publish events, manage RSVPs, and reach people across denominations. Your church page stays live in the meantime."
          cta="View plans"
        />
        <div className="mt-4">
          <Link to={`/churches/${slug}/dashboard`} className="text-[13px] text-muted no-underline hover:underline">
            ← Back to dashboard
          </Link>
        </div>
      </AdminShell>
    )
  }

  return (
    <AdminShell>
      <AdminPageHeader
        breadcrumb={['Events', 'New event']}
        eyebrow="Create"
        title="New event"
        sub="Choose an event type — the form below adjusts to what that type needs."
      />

      <form onSubmit={handleSubmit} noValidate>
        {serverError && (
          <div role="alert" className="px-4 py-3 rounded-xl mb-5 bg-danger-soft border border-danger-line text-danger-deep text-[13px]">
            {serverError}
          </div>
        )}

        {/* ── Type picker ─────────────────────────────────────────────────── */}
        <AdminCard style={{ marginBottom: 20 }}>
          <EventTypePicker value={eventType} onChange={(key) => handleTypeChange(key)} />
        </AdminCard>

        <div className="grid gap-5" style={{ gridTemplateColumns: '1.5fr 1fr' }}>

          {/* ── Left: main form ─────────────────────────────────────────────── */}
          <div className="flex flex-col gap-5">

            {/* Basics */}
            <AdminCard title="Basics">
              <div className="flex flex-col gap-4">
                <FormField label="Title" required htmlFor="title" error={errors.title}>
                  <TextInput
                    id="title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={denomConfig.eventNameSuggestions.potluck?.[0] ?? 'Fellowship lunch'}
                    aria-invalid={Boolean(errors.title)}
                  />
                </FormField>
                <FormField
                  label="Description"
                  hint="What the gathering is, who should come, what to expect."
                >
                  <AdminTextArea
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={`Long tables, warm ${denomConfig.signatureDish}, and dessert that lingers…`}
                  />
                </FormField>
                <FormField label="Tags" hint="Comma-separated · up to 5.">
                  <TextInput
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="vegetarian, gluten-free"
                  />
                  {denomConfig.suggestedTags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {denomConfig.suggestedTags.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => {
                            const current = tags.split(',').map((t) => t.trim()).filter(Boolean)
                            if (!current.includes(tag)) setTags([...current, tag].join(', '))
                          }}
                          className="h-[26px] px-2.5 rounded-full bg-primary-soft text-primary-deep border-0 cursor-pointer text-[11.5px] font-medium font-sans"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                </FormField>
              </div>
            </AdminCard>

            {/* When */}
            <AdminCard title="When" style={{ overflow: 'visible' }}>
              <div className="flex flex-col gap-3.5">
                <EventTimePicker
                  startsAt={startsAt}
                  endsAt={endsAt}
                  disablePast
                  onStartsAtChange={setStartsAt}
                  onEndsAtChange={setEndsAt}
                  startsAtError={errors.startsAt}
                />

                {/* Recurring */}
                <div className="p-3.5 rounded-xl bg-s2 border border-line-soft">
                  <div className={`flex items-center justify-between ${isRecurring ? 'mb-3.5' : ''}`}>
                    <div>
                      <div className="text-[13.5px] font-medium">Recurring schedule</div>
                      <div className="text-[11.5px] text-muted mt-0.5">Series creates an occurrence on each date.</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={isRecurring}
                      onChange={(e) => setIsRecurring(e.target.checked)}
                      aria-label="Recurring event"
                      className="w-[18px] h-[18px] cursor-pointer accent-primary"
                    />
                  </div>

                  {isRecurring && (
                    <div className="flex flex-col gap-3">
                      <div className="grid grid-cols-2 gap-3">
                        <FormField label="Frequency">
                          <select
                            value={freq}
                            onChange={(e) => setFreq(e.target.value as typeof freq)}
                            aria-label="Recurrence frequency"
                            className="input"
                          >
                            <option value="WEEKLY">Every week</option>
                            <option value="BIWEEKLY">Every 2 weeks</option>
                            <option value="MONTHLY">Every month</option>
                          </select>
                        </FormField>
                        <FormField label="Ends on">
                          <DatePicker
                            value={recurrenceEndsOn}
                            onChange={setRecurrenceEndsOn}
                            placeholder="No end date"
                          />
                        </FormField>
                      </div>

                      {freq !== 'MONTHLY' && (
                        <div>
                          <div className="text-[12px] font-semibold text-ink-2 mb-2">On</div>
                          <div className="flex gap-1.5 flex-wrap">
                            {DAYS.map((d, i) => (
                              <button
                                key={d.label}
                                type="button"
                                onClick={() => toggleDay(i)}
                                aria-label={d.label}
                                aria-pressed={selectedDays.includes(i)}
                                className={`w-[38px] h-[38px] rounded-full border-0 cursor-pointer font-sans text-[12px] font-semibold ${
                                  selectedDays.includes(i)
                                    ? 'bg-primary text-on-primary'
                                    : 'bg-surface text-ink-2 shadow-[0_0_0_1px_var(--line)]'
                                }`}
                              >
                                {d.label[0]}
                              </button>
                            ))}
                          </div>
                          {errors.days && <div className="text-[11.5px] text-[#A33A2A] mt-1.5">{errors.days}</div>}
                        </div>
                      )}

                      {preview.length > 0 && (
                        <div className="flex items-center gap-1.5 text-[11.5px] text-muted">
                          <AdmIcn name="cal" size={12} />
                          Next: <strong className="text-ink-2">
                            {preview.slice(0, 4).map((d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })).join(', ')}
                          </strong>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </AdminCard>

            {/* Where */}
            <AdminCard title="Where">
              <div className="flex flex-col gap-3.5">
                <div className="flex flex-col gap-2">
                  {/* Church / campus options */}
                  {[
                    { id: 'church', label: church?.name ?? 'Church address', sub: church ? churchAddress(church) : '' },
                    ...campuses.filter(c => c.type !== 'main').map(c => ({
                      id: `campus-${c.id}`,
                      label: c.name,
                      sub: [c.addressLine1, c.city, c.state].filter(Boolean).join(', '),
                    })),
                    { id: 'custom', label: 'Custom location', sub: 'Enter a different address' },
                  ].map((opt) => (
                    <label
                      key={opt.id}
                      className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl cursor-pointer border-[1.5px] transition-colors ${
                        selectedLocationId === opt.id
                          ? 'bg-primary-soft border-primary'
                          : 'bg-s2 border-line-soft hover:border-line'
                      }`}
                    >
                      <input
                        type="radio"
                        name="location"
                        value={opt.id}
                        checked={selectedLocationId === opt.id}
                        onChange={() => {
                          setSelectedLocationId(opt.id)
                          // Hiding the location is only available off-site; clear it
                          // when switching to the building or a campus.
                          if (opt.id !== 'custom') setHideAddressUntilApproved(false)
                        }}
                        className="accent-primary shrink-0"
                      />
                      <div>
                        <div className={`text-[13px] font-semibold ${selectedLocationId === opt.id ? 'text-primary-deep' : 'text-ink'}`}>
                          {opt.label}
                        </div>
                        {opt.sub && <div className="text-[11.5px] text-muted">{opt.sub}</div>}
                      </div>
                    </label>
                  ))}
                </div>
                {selectedLocationId === 'custom' && (
                  <LocationPicker
                    addressText={customAddress}
                    onAddressChange={setCustomAddress}
                    lat={customLat}
                    lng={customLng}
                    onCoordinatesChange={(lat, lng) => { setCustomLat(lat); setCustomLng(lng) }}
                    addressError={errors.customAddress}
                    locationName={customName}
                    onLocationNameChange={setCustomName}
                    placeholder="Enter venue address"
                  />
                )}
              </div>
            </AdminCard>

            {/* Capability toggles */}
            <CapabilityToggleRow
              bringADish={Boolean(attributes.bringADish)}
              outdoorActivity={Boolean(attributes.outdoorActivity)}
              onToggleDish={() => setAttributes((p) => ({ ...p, bringADish: p.bringADish ? undefined : true }))}
              onToggleOutdoor={() => setAttributes((p) => ({ ...p, outdoorActivity: p.outdoorActivity ? undefined : true }))}
            />

            {/* Dish goals */}
            {attributes.bringADish && (
              <AdminCard
                title={isRecurring ? 'Dish goal template · applied to each occurrence' : 'Dish goals'}
                action={isRecurring
                  ? <span className="mono text-[10px] text-muted tracking-[0.08em]">APPLIED TO EACH OCCURRENCE</span>
                  : undefined
                }
              >
                <DishGoalsFields
                  mainGoal={mainGoal} setMainGoal={setMainGoal}
                  saladGoal={saladGoal} setSaladGoal={setSaladGoal}
                  sideGoal={sideGoal} setSideGoal={setSideGoal}
                  dessertGoal={dessertGoal} setDessertGoal={setDessertGoal}
                  drinkGoal={drinkGoal} setDrinkGoal={setDrinkGoal}
                />
              </AdminCard>
            )}

            {/* Outdoor details */}
            {attributes.outdoorActivity && (
              <AdminCard title="Outdoor details">
                <OutdoorDetailsFields
                  difficulty={outdoorDifficulty} setDifficulty={setOutdoorDifficulty}
                  distance={outdoorDistance} setDistance={setOutdoorDistance}
                  elevation={outdoorElevation} setElevation={setOutdoorElevation}
                  meetupNote={outdoorMeetupNote} setMeetupNote={setOutdoorMeetupNote}
                />
              </AdminCard>
            )}

            {/* Study details */}
            {eventType === 'study' && (
              <AdminCard>
                <div className="eyebrow mb-3">Study details</div>
                <div className="flex flex-col gap-3.5">
                  <FormField label="Topic" htmlFor="studyTopic">
                    <input
                      id="studyTopic"
                      type="text"
                      value={studyTopic}
                      onChange={(e) => setStudyTopic(e.target.value)}
                      placeholder="The Sermon on the Mount"
                      className="input"
                    />
                  </FormField>
                  <FormField label="Scripture reference" htmlFor="studyScripture">
                    <input
                      id="studyScripture"
                      type="text"
                      value={studyScripture}
                      onChange={(e) => setStudyScripture(e.target.value)}
                      placeholder="Matthew 5–7"
                      className="input"
                    />
                  </FormField>
                </div>
              </AdminCard>
            )}
          </div>

          {/* ── Right: settings rail ─────────────────────────────────────────── */}
          <div className="flex flex-col gap-5">

            <AdminCard title="Visibility & RSVPs">
              <div className="flex flex-col gap-4">
                {church?.denomination && church.denomination !== 'other' && (
                  <div>
                    <div className="text-[12px] font-semibold text-ink-2 mb-2">Who can see this event?</div>
                    <div className="flex gap-2">
                      {([
                        { value: 'everyone', label: 'Everyone', sub: 'Visible to all app users' },
                        { value: 'denomination', label: `${church.denomination.replace(/-/g, ' ')} only`, sub: 'Only users who share your denomination' },
                      ] as const).map(({ value, label, sub }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setAudience(value)}
                          style={{
                            flex: 1, padding: '8px 10px', borderRadius: 10, textAlign: 'left',
                            border: `${audience === value ? 2 : 1}px solid ${audience === value ? 'var(--primary)' : 'var(--line-soft)'}`,
                            background: audience === value ? 'var(--primary-soft)' : 'var(--surface)',
                            cursor: 'pointer', fontFamily: 'inherit',
                          }}
                        >
                          <div className="text-[12.5px] font-semibold text-ink capitalize">{label}</div>
                          <div className="text-[11px] text-muted mt-0.5">{sub}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <FormField label="Max guests" hint="Leave blank for no limit." htmlFor="maxAttendees">
                  <TextInput
                    id="maxAttendees"
                    type="number"
                    min={1}
                    value={maxAttendees}
                    onChange={(e) => setMaxAttendees(e.target.value)}
                    placeholder="No limit"
                    suffix="people"
                  />
                </FormField>
                <Toggle
                  on={rsvpRequiresApproval}
                  onChange={(v) => {
                    setRsvpRequiresApproval(v)
                    if (!v) setHideAddressUntilApproved(false)
                  }}
                  label="Require RSVP approval"
                  sub="You approve each guest before they're confirmed."
                />
                {rsvpRequiresApproval && selectedLocationId === 'custom' && (
                  <Toggle
                    on={hideAddressUntilApproved}
                    onChange={setHideAddressUntilApproved}
                    label="Hide location until approved"
                    sub="Guests see only the general area. You share the exact location when you approve their RSVP."
                  />
                )}
                {rsvpRequiresApproval && selectedLocationId !== 'custom' && (
                  <div className="text-[11.5px] text-muted px-0.5">
                    This location is public. Pick a custom location to hide it until approval.
                  </div>
                )}
              </div>
            </AdminCard>

            {rsvpRequiresApproval && (
              <AdminCard title="Reminders">
                <div className="flex flex-col gap-3">
                  <Toggle on={remindDayBefore} onChange={setRemindDayBefore} label="Day-before reminder" sub="Sent to confirmed RSVPs at 9 AM the day before." />
                  <Toggle
                    on={remindMorningOf}
                    onChange={setRemindMorningOf}
                    label="Morning-of reminder"
                    sub={attributes.bringADish
                      ? "Sent day-of with address and dish slot status."
                      : "Sent day-of with the event address."}
                  />
                  {attributes.bringADish && (
                    <Toggle on={remindDishGap} onChange={setRemindDishGap} label="Dish gap broadcast" sub="Auto-send if coverage is < 70% by 5 PM the day before." />
                  )}
                </div>
              </AdminCard>
            )}

            <AdminCard title="Photo">
              {imageUrl ? (
                <div className="flex flex-col gap-2.5">
                  <div className="rounded-xl overflow-hidden aspect-[3/2]">
                    <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </div>
                  <div className="flex gap-4 items-center">
                    <ImageUpload context="event-image" onUpload={setImageUrl}>
                      <span className="text-[13px] text-primary font-medium cursor-pointer font-sans">Change photo</span>
                    </ImageUpload>
                    <button type="button" onClick={() => setImageUrl(null)} style={{ fontSize: 12, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} className="font-sans">
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <ImageUpload context="event-image" onUpload={setImageUrl}>
                  <div style={{ height: 90, border: '1.5px dashed var(--line-soft)', borderRadius: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer' }}>
                    <AdmIcn name="image" size={20} />
                    <div className="text-[12.5px] font-semibold text-ink-2">Add a photo</div>
                    <div className="text-[11px] text-muted">JPEG, PNG, or WebP · 5 MB max</div>
                  </div>
                </ImageUpload>
              )}
            </AdminCard>

          </div>
        </div>

        {Object.keys(errors).length > 0 && (
          <div className="mt-7">
            <MissingFieldsNotice errors={errors} />
          </div>
        )}

        {/* ── Sticky publish bar ──────────────────────────────────────────── */}
        <div
          className={`${Object.keys(errors).length > 0 ? 'mt-3' : 'mt-7'} px-[18px] py-3.5 bg-surface border border-line-soft rounded-2xl flex items-center gap-4`}
          style={{ boxShadow: '0 10px 28px -16px rgba(60,40,10,0.25)' }}
        >
          <div className="flex-1">
            <div className="text-[13px] font-semibold">Ready to publish?</div>
            <div className="text-[11.5px] text-muted">
              {isRecurring
                ? 'Creates weekly occurrences · first goes live immediately.'
                : 'Goes live immediately on your church profile.'}
            </div>
          </div>
          <AdmBtn type="button" variant="ghost" onClick={() => setShowPreview(true)}>
            Preview
          </AdmBtn>
          <AdmBtn
            type="button"
            variant="ghost"
            disabled={createEvent.isPending}
            onClick={handleSaveDraft}
          >
            Save as draft
          </AdmBtn>
          <AdmBtn
            type="submit"
            variant="primary"
            size="lg"
            disabled={createEvent.isPending || !church}
          >
            {createEvent.isPending ? 'Creating…' : 'Publish event'}
          </AdmBtn>
        </div>
      </form>

      {showPreview && church && (
        <EventPreviewModal
          title={title}
          description={description}
          eventType={eventType}
          startsAt={startsAt}
          endsAt={endsAt}
          churchName={church.name}
          locationName={resolveLocationName(selectedLocationId, campuses, customName)}
          addressText={resolveLocationAddress(selectedLocationId, church, campuses, customAddress)}
          imageUrl={imageUrl}
          onClose={() => setShowPreview(false)}
        />
      )}
    </AdminShell>
  )
}
